
import { useState, useRef, useCallback, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import LanguageSelector, { LANGUAGES } from './components/LanguageSelector';
import RecordingControls from './components/RecordingControls';
import TranscriptPanel, { TranscriptEntry } from './components/TranscriptPanel';

// Demo transcript samples for simulation
const DEMO_PHRASES: Record<string, { original: string; translations: Record<string, string> }[]> = {
  en: [
    {
      original: 'Hello, welcome to the voice translation tool. This is a live demonstration.',
      translations: {
        es: 'Hola, bienvenido a la herramienta de traducción de voz. Esta es una demostración en vivo.',
        fr: 'Bonjour, bienvenue dans l\'outil de traduction vocale. Ceci est une démonstration en direct.',
        de: 'Hallo, willkommen beim Sprachübersetzungstool. Dies ist eine Live-Demonstration.',
        it: 'Ciao, benvenuto nello strumento di traduzione vocale. Questa è una dimostrazione dal vivo.',
        pt: 'Olá, bem-vindo à ferramenta de tradução de voz. Esta é uma demonstração ao vivo.',
        zh: '你好，欢迎使用语音翻译工具。这是一个实时演示。',
        ja: 'こんにちは、音声翻訳ツールへようこそ。これはライブデモンストレーションです。',
        ko: '안녕하세요, 음성 번역 도구에 오신 것을 환영합니다. 이것은 라이브 데모입니다.',
        ar: 'مرحباً، مرحباً بك في أداة الترجمة الصوتية. هذا عرض توضيحي مباشر.',
        ru: 'Привет, добро пожаловать в инструмент голосового перевода. Это живая демонстрация.',
        hi: 'नमस्ते, वॉयस ट्रांसलेशन टूल में आपका स्वागत है। यह एक लाइव प्रदर्शन है।',
        tr: 'Merhaba, ses çeviri aracına hoş geldiniz. Bu canlı bir gösterimdir.',
        nl: 'Hallo, welkom bij de spraakvertaaltool. Dit is een live demonstratie.',
        pl: 'Cześć, witamy w narzędziu do tłumaczenia głosowego. To jest demonstracja na żywo.',
        sv: 'Hej, välkommen till röstöversättningsverktyget. Det här är en livedemonstation.',
        th: 'สวัสดี ยินดีต้อนรับสู่เครื่องมือแปลเสียง นี่คือการสาธิตสด',
        vi: 'Xin chào, chào mừng bạn đến với công cụ dịch giọng nói. Đây là bản trình diễn trực tiếp.',
        id: 'Halo, selamat datang di alat terjemahan suara. Ini adalah demonstrasi langsung.',
      },
    },
    {
      original: 'I can translate your speech into over thirty different languages in real time.',
      translations: {
        es: 'Puedo traducir tu discurso a más de treinta idiomas diferentes en tiempo real.',
        fr: 'Je peux traduire votre discours en plus de trente langues différentes en temps réel.',
        de: 'Ich kann Ihre Sprache in Echtzeit in über dreißig verschiedene Sprachen übersetzen.',
        it: 'Posso tradurre il tuo discorso in oltre trenta lingue diverse in tempo reale.',
        pt: 'Posso traduzir seu discurso para mais de trinta idiomas diferentes em tempo real.',
        zh: '我可以实时将您的语音翻译成三十多种不同的语言。',
        ja: 'リアルタイムで30以上の異なる言語にスピーチを翻訳できます。',
        ko: '실시간으로 30개 이상의 다양한 언어로 음성을 번역할 수 있습니다.',
        ar: 'يمكنني ترجمة كلامك إلى أكثر من ثلاثين لغة مختلفة في الوقت الفعلي.',
        ru: 'Я могу переводить вашу речь на более чем тридцать различных языков в режиме реального времени.',
        hi: 'मैं आपके भाषण को वास्तविक समय में तीस से अधिक विभिन्न भाषाओं में अनुवाद कर सकता हूं।',
        tr: 'Konuşmanızı gerçek zamanlı olarak otuz\'dan fazla farklı dile çevirebilirim.',
        nl: 'Ik kan uw spraak in realtime vertalen naar meer dan dertig verschillende talen.',
        pl: 'Mogę tłumaczyć Twoją mowę na ponad trzydzieści różnych języków w czasie rzeczywistym.',
        sv: 'Jag kan översätta ditt tal till över trettio olika språk i realtid.',
        th: 'ฉันสามารถแปลคำพูดของคุณเป็นภาษาต่างๆ กว่าสามสิบภาษาแบบเรียลไทม์',
        vi: 'Tôi có thể dịch lời nói của bạn sang hơn ba mươi ngôn ngữ khác nhau trong thời gian thực.',
        id: 'Saya dapat menerjemahkan ucapan Anda ke lebih dari tiga puluh bahasa berbeda secara real time.',
      },
    },
    {
      original: 'The transcript is saved automatically and can be exported as a text file.',
      translations: {
        es: 'La transcripción se guarda automáticamente y se puede exportar como archivo de texto.',
        fr: 'La transcription est sauvegardée automatiquement et peut être exportée en fichier texte.',
        de: 'Das Transkript wird automatisch gespeichert und kann als Textdatei exportiert werden.',
        it: 'La trascrizione viene salvata automaticamente e può essere esportata come file di testo.',
        pt: 'A transcrição é salva automaticamente e pode ser exportada como arquivo de texto.',
        zh: '转录内容会自动保存，可以导出为文本文件。',
        ja: 'トランスクリプトは自動的に保存され、テキストファイルとしてエクスポートできます。',
        ko: '트랜스크립트는 자동으로 저장되며 텍스트 파일로 내보낼 수 있습니다.',
        ar: 'يتم حفظ النص تلقائياً ويمكن تصديره كملف نصي.',
        ru: 'Транскрипт сохраняется автоматически и может быть экспортирован в виде текстового файла.',
        hi: 'ट्रांसक्रिप्ट स्वचालित रूप से सहेजा जाता है और टेक्स्ट फ़ाइल के रूप में निर्यात किया जा सकता है।',
        tr: 'Transkript otomatik olarak kaydedilir ve metin dosyası olarak dışa aktarılabilir.',
        nl: 'Het transcript wordt automatisch opgeslagen en kan worden geëxporteerd als tekstbestand.',
        pl: 'Transkrypt jest zapisywany automatycznie i może być eksportowany jako plik tekstowy.',
        sv: 'Transkriptet sparas automatiskt och kan exporteras som en textfil.',
        th: 'การถอดความจะถูกบันทึกโดยอัตโนมัติและสามารถส่งออกเป็นไฟล์ข้อความได้',
        vi: 'Bản ghi được lưu tự động và có thể xuất dưới dạng tệp văn bản.',
        id: 'Transkrip disimpan secara otomatis dan dapat diekspor sebagai file teks.',
      },
    },
    {
      original: 'You can pause and resume recording at any time without losing your progress.',
      translations: {
        es: 'Puedes pausar y reanudar la grabación en cualquier momento sin perder tu progreso.',
        fr: 'Vous pouvez mettre en pause et reprendre l\'enregistrement à tout moment sans perdre votre progression.',
        de: 'Sie können die Aufnahme jederzeit pausieren und fortsetzen, ohne Ihren Fortschritt zu verlieren.',
        it: 'Puoi mettere in pausa e riprendere la registrazione in qualsiasi momento senza perdere i progressi.',
        pt: 'Você pode pausar e retomar a gravação a qualquer momento sem perder seu progresso.',
        zh: '您可以随时暂停和恢复录音，而不会丢失进度。',
        ja: '進行状況を失うことなく、いつでも録音を一時停止および再開できます。',
        ko: '진행 상황을 잃지 않고 언제든지 녹음을 일시 중지하고 재개할 수 있습니다.',
        ar: 'يمكنك إيقاف التسجيل مؤقتاً واستئنافه في أي وقت دون فقدان تقدمك.',
        ru: 'Вы можете приостановить и возобновить запись в любое время, не теряя прогресса.',
        hi: 'आप अपनी प्रगति खोए बिना किसी भी समय रिकॉर्डिंग को रोक और फिर से शुरू कर सकते हैं।',
        tr: 'İlerlemenizi kaybetmeden istediğiniz zaman kaydı duraklatabilir ve devam ettirebilirsiniz.',
        nl: 'U kunt de opname op elk moment pauzeren en hervatten zonder uw voortgang te verliezen.',
        pl: 'Możesz wstrzymać i wznowić nagrywanie w dowolnym momencie bez utraty postępu.',
        sv: 'Du kan pausa och återuppta inspelningen när som helst utan att förlora dina framsteg.',
        th: 'คุณสามารถหยุดชั่วคราวและกลับมาบันทึกได้ทุกเมื่อโดยไม่สูญเสียความคืบหน้า',
        vi: 'Bạn có thể tạm dừng và tiếp tục ghi âm bất cứ lúc nào mà không mất tiến trình.',
        id: 'Anda dapat menjeda dan melanjutkan perekaman kapan saja tanpa kehilangan kemajuan Anda.',
      },
    },
  ],
};

function getTranslation(phrase: { original: string; translations: Record<string, string> }, targetLang: string): string {
  if (targetLang === 'en') return phrase.original;
  return phrase.translations[targetLang] || `[${LANGUAGES.find(l => l.code === targetLang)?.name || targetLang} translation of: "${phrase.original}"]`;
}

export default function VoiceTranslatorPage() {
  const [sourceLang, setSourceLang] = useState('en');
  const [targetLang, setTargetLang] = useState('es');
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [entries, setEntries] = useState<TranscriptEntry[]>([]);
  const [isTranslating, setIsTranslating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'transcript' | 'settings'>('transcript');

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const phraseIndexRef = useRef(0);
  const phraseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (phraseTimerRef.current) clearTimeout(phraseTimerRef.current);
  };

  const scheduleNextPhrase = useCallback((currentDuration: number) => {
    const phrases = DEMO_PHRASES['en'] || [];
    if (phraseIndexRef.current >= phrases.length) return;

    const delay = 3500 + Math.random() * 1500;
    phraseTimerRef.current = setTimeout(() => {
      const phrase = phrases[phraseIndexRef.current];
      if (!phrase) return;

      setIsTranslating(true);
      setTimeout(() => {
        const entry: TranscriptEntry = {
          id: `entry-${Date.now()}`,
          timestamp: currentDuration,
          originalText: phrase.original,
          translatedText: getTranslation(phrase, targetLang),
          sourceLang,
          targetLang,
        };
        setEntries(prev => [...prev, entry]);
        setIsTranslating(false);
        phraseIndexRef.current += 1;
        scheduleNextPhrase(currentDuration + Math.floor(delay / 1000));
      }, 1200);
    }, delay);
  }, [sourceLang, targetLang]);

  const handleStart = useCallback(() => {
    setIsRecording(true);
    setIsPaused(false);
    setDuration(0);
    phraseIndexRef.current = 0;

    timerRef.current = setInterval(() => {
      setDuration(d => d + 1);
    }, 1000);

    scheduleNextPhrase(0);
  }, [scheduleNextPhrase]);

  const handlePause = useCallback(() => {
    setIsPaused(true);
    if (timerRef.current) clearInterval(timerRef.current);
    if (phraseTimerRef.current) clearTimeout(phraseTimerRef.current);
  }, []);

  const handleResume = useCallback(() => {
    setIsPaused(false);
    timerRef.current = setInterval(() => {
      setDuration(d => d + 1);
    }, 1000);
    scheduleNextPhrase(duration);
  }, [duration, scheduleNextPhrase]);

  const handleStop = useCallback(() => {
    setIsRecording(false);
    setIsPaused(false);
    setIsTranslating(false);
    clearTimers();
  }, []);

  useEffect(() => {
    return () => clearTimers();
  }, []);

  const handleCopy = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  const handleClear = useCallback(() => {
    setEntries([]);
    phraseIndexRef.current = 0;
  }, []);

  const handleExport = useCallback(() => {
    const srcLang = LANGUAGES.find(l => l.code === sourceLang)?.name || sourceLang;
    const tgtLang = LANGUAGES.find(l => l.code === targetLang)?.name || targetLang;
    const lines: string[] = [
      `Voice Translation Transcript`,
      `Source Language: ${srcLang}`,
      `Target Language: ${tgtLang}`,
      `Generated: ${new Date().toLocaleString()}`,
      `Duration: ${Math.floor(duration / 60)}m ${duration % 60}s`,
      '',
      '='.repeat(60),
      '',
    ];
    entries.forEach((e, i) => {
      lines.push(`[${String(i + 1).padStart(2, '0')}] ${String(Math.floor(e.timestamp / 60)).padStart(2, '0')}:${String(e.timestamp % 60).padStart(2, '0')}`);
      lines.push(`${srcLang}: ${e.originalText}`);
      lines.push(`${tgtLang}: ${e.translatedText}`);
      lines.push('');
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [entries, sourceLang, targetLang, duration]);

  const swapLanguages = () => {
    setSourceLang(targetLang);
    setTargetLang(sourceLang);
  };

  const sourceLangInfo = LANGUAGES.find(l => l.code === sourceLang);
  const targetLangInfo = LANGUAGES.find(l => l.code === targetLang);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-10 px-6 bg-gradient-to-br from-teal-50 via-white to-emerald-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-6">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-translate-2 text-base"></i>
            </div>
            AI-Powered Voice Translation
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 mb-5 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Speak. Translate. <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-500">Understand.</span>
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto mb-8">
            Record your voice and get instant transcription with real-time translation into 30+ languages. Perfect for meetings, travel, and multilingual communication.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
            {[
              { icon: 'ri-global-line', text: '30+ Languages' },
              { icon: 'ri-time-line', text: 'Real-time Translation' },
              { icon: 'ri-file-text-line', text: 'Full Transcript' },
              { icon: 'ri-download-line', text: 'Export to TXT' },
            ].map(f => (
              <div key={f.text} className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center text-teal-500">
                  <i className={`${f.icon} text-base`}></i>
                </div>
                <span>{f.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Tool */}
      <section className="py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Language Bar */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 mb-6">
            <div className="flex flex-wrap items-end justify-center gap-4">
              <LanguageSelector value={sourceLang} onChange={setSourceLang} label="Speak In" />

              <div className="flex flex-col items-center gap-1 pb-1">
                <button
                  onClick={swapLanguages}
                  className="w-10 h-10 flex items-center justify-center bg-teal-50 hover:bg-teal-100 border-2 border-teal-200 rounded-xl text-teal-600 hover:scale-110 transition-all cursor-pointer"
                  title="Swap languages"
                >
                  <i className="ri-arrow-left-right-line text-lg"></i>
                </button>
              </div>

              <LanguageSelector value={targetLang} onChange={setTargetLang} label="Translate To" />

              <div className="flex-1 min-w-[200px]">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Translation Preview</p>
                <div className="flex items-center gap-3 px-4 py-3 bg-teal-50 border-2 border-teal-100 rounded-xl">
                  <span className="text-2xl">{sourceLangInfo?.flag}</span>
                  <div className="w-4 h-4 flex items-center justify-center text-teal-400">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                  <span className="text-2xl">{targetLangInfo?.flag}</span>
                  <span className="text-sm font-semibold text-teal-700">
                    {sourceLangInfo?.name} &rarr; {targetLangInfo?.name}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Panel */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Recording */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              {/* Recording Card */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg">
                    <i className="ri-mic-line text-teal-600 text-lg"></i>
                  </div>
                  <h2 className="font-bold text-gray-800">Voice Recorder</h2>
                </div>
                <RecordingControls
                  isRecording={isRecording}
                  isPaused={isPaused}
                  duration={duration}
                  onStart={handleStart}
                  onPause={handlePause}
                  onResume={handleResume}
                  onStop={handleStop}
                />
              </div>

              {/* Stats Card */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
                <h3 className="font-bold text-gray-800 text-sm mb-4">Session Stats</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Segments', value: entries.length, icon: 'ri-list-check', color: 'text-teal-600 bg-teal-50' },
                    { label: 'Words', value: entries.reduce((a, e) => a + e.originalText.split(' ').length, 0), icon: 'ri-text', color: 'text-emerald-600 bg-emerald-50' },
                    { label: 'Duration', value: `${Math.floor(duration / 60)}m ${duration % 60}s`, icon: 'ri-time-line', color: 'text-cyan-600 bg-cyan-50' },
                    { label: 'Languages', value: 2, icon: 'ri-global-line', color: 'text-indigo-600 bg-indigo-50' },
                  ].map(stat => (
                    <div key={stat.label} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${stat.color}`}>
                        <i className={`${stat.icon} text-sm`}></i>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-gray-800 leading-none">{stat.value}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tips */}
              <div className="bg-gradient-to-br from-teal-500 to-emerald-500 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-lightbulb-line text-lg"></i>
                  </div>
                  <h3 className="font-bold text-sm">Tips for Best Results</h3>
                </div>
                <ul className="space-y-2.5 text-sm text-white/90">
                  {[
                    'Speak clearly at a moderate pace',
                    'Minimize background noise',
                    'Use a quality microphone',
                    'Pause between sentences',
                    'Export transcript after session',
                  ].map(tip => (
                    <li key={tip} className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-check-line text-sm text-white/70"></i>
                      </div>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right: Transcript */}
            <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden" style={{ minHeight: '600px' }}>
              {/* Tabs */}
              <div className="flex border-b border-gray-100 px-1 py-1 bg-gray-50">
                {(['transcript', 'settings'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-all cursor-pointer capitalize whitespace-nowrap ${
                      activeTab === tab
                        ? 'bg-white text-teal-600 shadow-sm'
                        : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {tab === 'transcript' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-file-text-line"></i> Transcript
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-settings-3-line"></i> Settings
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {activeTab === 'transcript' ? (
                <div style={{ height: '560px' }}>
                  <TranscriptPanel
                    entries={entries}
                    isTranslating={isTranslating}
                    onCopy={handleCopy}
                    onClear={handleClear}
                    onExport={handleExport}
                  />
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  <div>
                    <h3 className="font-bold text-gray-800 mb-4">Translation Settings</h3>
                    <div className="space-y-4">
                      {[
                        { label: 'Auto-detect source language', desc: 'Automatically identify the spoken language', enabled: false },
                        { label: 'Show confidence score', desc: 'Display translation accuracy percentage', enabled: true },
                        { label: 'Phonetic transcription', desc: 'Show pronunciation guide for target language', enabled: false },
                        { label: 'Auto-save transcript', desc: 'Automatically save transcript to browser storage', enabled: true },
                        { label: 'Noise cancellation', desc: 'Filter background noise for cleaner audio', enabled: true },
                        { label: 'Punctuation detection', desc: 'Automatically add punctuation to transcript', enabled: true },
                      ].map(setting => (
                        <SettingToggle key={setting.label} {...setting} />
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-bold text-gray-800 mb-4">Export Options</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { icon: 'ri-file-text-line', label: 'Plain Text (.txt)', color: 'text-gray-600' },
                        { icon: 'ri-file-word-line', label: 'Word Document (.docx)', color: 'text-cyan-600' },
                        { icon: 'ri-file-pdf-line', label: 'PDF Document (.pdf)', color: 'text-red-500' },
                        { icon: 'ri-file-excel-line', label: 'Spreadsheet (.csv)', color: 'text-emerald-600' },
                      ].map(opt => (
                        <button
                          key={opt.label}
                          onClick={opt.label.includes('txt') ? handleExport : undefined}
                          className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:border-teal-300 hover:bg-teal-50 transition-all cursor-pointer text-left"
                        >
                          <div className={`w-8 h-8 flex items-center justify-center ${opt.color}`}>
                            <i className={`${opt.icon} text-xl`}></i>
                          </div>
                          <span className="text-xs font-medium text-gray-700">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Copied Toast */}
      {copied && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 bg-gray-900 text-white rounded-xl shadow-2xl text-sm font-medium animate-[fadeInUp_0.3s_ease]">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-check-line text-teal-400"></i>
          </div>
          Copied to clipboard!
        </div>
      )}

      {/* Supported Languages Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            30+ Supported Languages
          </h2>
          <p className="text-gray-500 mb-10">Translate between any combination of these languages instantly</p>
          <div className="flex flex-wrap justify-center gap-3">
            {LANGUAGES.map(lang => (
              <div
                key={lang.code}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:border-teal-400 hover:text-teal-700 transition-all cursor-default shadow-sm"
              >
                <span>{lang.flag}</span>
                <span>{lang.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Perfect For Every Situation
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-briefcase-line',
                title: 'Business Meetings',
                desc: 'Translate multilingual meetings in real time. Keep everyone aligned regardless of language.',
                color: 'from-teal-400 to-emerald-400',
              },
              {
                icon: 'ri-plane-line',
                title: 'Travel & Tourism',
                desc: 'Communicate effortlessly abroad. Translate conversations with locals instantly.',
                color: 'from-cyan-400 to-teal-400',
              },
              {
                icon: 'ri-graduation-cap-line',
                title: 'Education',
                desc: 'Support multilingual classrooms. Transcribe lectures and translate for international students.',
                color: 'from-emerald-400 to-green-400',
              },
              {
                icon: 'ri-hospital-line',
                title: 'Healthcare',
                desc: 'Bridge language gaps between patients and providers for better care outcomes.',
                color: 'from-teal-400 to-cyan-400',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Customer Support',
                desc: 'Serve global customers in their native language with real-time translation.',
                color: 'from-green-400 to-teal-400',
              },
              {
                icon: 'ri-team-line',
                title: 'Remote Teams',
                desc: 'Collaborate across borders. Translate team calls and keep transcripts for reference.',
                color: 'from-cyan-400 to-emerald-400',
              },
            ].map(uc => (
              <div key={uc.title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 hover:shadow-lg transition-shadow">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${uc.color} text-white mb-4`}>
                  <i className={`${uc.icon} text-xl`}></i>
                </div>
                <h3 className="font-bold text-gray-800 mb-2">{uc.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function SettingToggle({ label, desc, enabled: defaultEnabled }: { label: string; desc: string; enabled: boolean }) {
  const [enabled, setEnabled] = useState(defaultEnabled);
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
      <div>
        <p className="text-sm font-semibold text-gray-800">{label}</p>
        <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
      </div>
      <button
        onClick={() => setEnabled(!enabled)}
        className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ml-4 ${enabled ? 'bg-teal-500' : 'bg-gray-300'}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`}
        />
      </button>
    </div>
  );
}
